import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { MetricDetailsTab } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ForecastsDrawerPopupComponent from 'webapp/components/forecasts/drawer-popup';
import ForecastsMyGridItemComponent from 'webapp/components/forecasts/my/grid-item';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { Forecast, MetricSetup } from 'webapp/models-custom';

interface Args {
	metricSetup: MetricSetup;
	excludeForecastId?: string | number | null;
	title?: string;
	hideEmpty?: boolean;
}

export default class MetricDetailsRelatedForecastsComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/forecasts')
	declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		void this.mmForecastsService.requestForecastsData();
	}

	@cached
	get relatedForecasts(): Forecast[] {
		const key = this.args.metricSetup.key;
		if (!key) return [];
		const excludeId = this.args.excludeForecastId;
		return this.mmForecastsService.forecastModels.filter(
			(f) => f.metricSetupKey === key && (!excludeId || f.id !== String(excludeId)),
		);
	}

	get title(): string {
		return this.args.title || `Saved forecasts scenarios for ${this.metricTitle}`;
	}

	get metricTitle(): string {
		return this.args.metricSetup.metric?.name || this.args.metricSetup.key || '';
	}

	get forecastButtonText(): string {
		return this.metricTitle ? `Forecast ${this.metricTitle}` : 'Forecast';
	}

	get hasAnySavedForecasts(): boolean {
		return this.mmForecastsService.forecastModels.length > 0;
	}

	@action
	openDetails(model: Forecast): void {
		this.mmForecastsService.openForecastDetails(model);
	}

	@action
	recalculate(model: Forecast): void {
		this.mmForecastsService.recalculateForecast(model);
	}

	@action
	deleteForecast(model: Forecast): void {
		this.mmForecastsService.deleteForecast(model);
	}

	@action
	switchToForecastTab(): void {
		const handled = this.eventManagerService.trigger(
			AppEvent.FORECAST_DRAWER_SWITCH_TO_FORECAST_TAB,
			{},
		);

		if (!handled) {
			this.metricDetailsService.openMetricDetailsDrawer({
				metricSetup: this.args.metricSetup,
				tab: MetricDetailsTab.FORECAST,
			});
		}
	}

	@action
	openAllForecasts(): void {
		const popup = this.popupService.createModal(ForecastsDrawerPopupComponent, {
			popupClass: 'popup-type-drawer popup-type-forecasts-drawer',
			showClose: false,
			hasOverlay: true,
			closeOnOverlayClick: false,
			allowStacked: true,
			isAutoCentered: false,
			autofocus: false,
		});
		popup.open({});
	}

	get isForecastingEnabled(): boolean {
		return this.billingService.forecastsEnabled;
	}

	get hasForecasts(): boolean {
		return this.isForecastingEnabled && !!this.relatedForecasts.length;
	}

	get showSection(): boolean {
		if (!this.args.metricSetup.metric || !this.isForecastingEnabled) {
			return false;
		}
		return !this.args.hideEmpty || !!this.relatedForecasts.length;
	}

	<template>
		{{#if this.showSection}}
			<div class="module-box metric-explorer-related-forecasts">
				{{#if this.hasForecasts}}
					<div class="module-box__header">
						<div class="module-box__title">
							<CommonUiIconComponent @name="Forecasts" @size="md" />
							<span>{{this.title}}</span>
						</div>
						<SubmitButtonComponent
							@theme="blue-outlined"
							@text="Open all saved forecasts"
							@icon="FolderOpen"
							@onTrigger={{this.openAllForecasts}}
						/>
					</div>
					<div class="module-box__divider" />
					<ul class="metric-explorer-related-forecasts__cards">
						{{#each this.relatedForecasts as |forecast|}}
							<ForecastsMyGridItemComponent
								@model={{forecast}}
								@hideSort={{true}}
								@skipVisibilityCheck={{true}}
								@openDetails={{this.openDetails}}
								@recalculate={{this.recalculate}}
								@delete={{this.deleteForecast}}
							/>
						{{/each}}
					</ul>
				{{else}}
					<div class="metric-explorer-related-forecasts__empty">
						<CommonUiIconComponent @name="Search" @size="2xl" />
						<div class="metric-explorer-related-forecasts__empty-text">
							<span class="title">
								Plan with data, not gut feeling
							</span>
							<span class="subtitle">
								Simulate different forecasting scenarios, test whether your goals
								are achievable, and uncover hidden dependencies.
							</span>
						</div>
						<div class="metric-explorer-related-forecasts__empty-actions">
							<SubmitButtonComponent
								@theme="blue"
								@text={{this.forecastButtonText}}
								@icon="Forecasts"
								@onTrigger={{this.switchToForecastTab}}
							/>
							{{#if this.hasAnySavedForecasts}}
								<SubmitButtonComponent
									@theme="blue-outlined"
									@text="Open all saved forecasts"
									@icon="FolderOpen"
									@onTrigger={{this.openAllForecasts}}
								/>
							{{/if}}
						</div>
					</div>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
